import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Avatar,
  Space,
  Typography,
  message,
  Tag,
  Input,
  Select,
  Tooltip,
  Modal,
  Grid,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  EyeOutlined,
  CopyOutlined,
  SearchOutlined,
  FilterOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Post, Page, RelatedPost } from "../../types";
import PageSelect from "../../components/Shared/PageSelect";
import { postsService } from "../../services/posts.service";
import { pagesService } from "../../services/pages.service";
import { Popconfirm } from "antd";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const PostList: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [filterPageId, setFilterPageId] = useState<string | undefined>(
    undefined,
  );
  const [searchText, setSearchText] = useState("");
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    fetchPosts();
    fetchPages();
  }, [filterPageId]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = filterPageId
        ? await postsService.getByPage(filterPageId)
        : await postsService.getAll();
      setPosts(data);
    } catch (error) {
      console.error("Failed to fetch posts", error);
      message.error("Không thể tải danh sách bài viết");
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const data = await pagesService.getAll();
      setPages(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (postId: string) => {
    try {
      await postsService.delete(postId);
      message.success("Đã xóa bài viết khỏi hệ thống");
      fetchPosts();
    } catch (error) {
      console.error("Failed to delete post", error);
      message.error("Không thể xóa bài viết");
    }
  };

  const getPostIcon = (type: string) => {
    switch (type) {
      case "photo":
      case "album":
        return <PictureOutlined style={{ color: "#eb2f96" }} />;
      case "video":
        return <VideoCameraOutlined style={{ color: "#722ed1" }} />;
      case "link":
      case "share":
        return <LinkOutlined style={{ color: "#1890ff" }} />;
      case "status":
      case "text":
        return <FileTextOutlined style={{ color: "#52c41a" }} />;
      default:
        return <FileTextOutlined style={{ color: "#52c41a" }} />;
    }
  };

  const getPostTypeText = (type: string) => {
    switch (type) {
      case "photo":
        return "Hình ảnh";
      case "video":
        return "Video";
      case "link":
      case "share":
        return "Liên kết";
      case "status":
      case "text":
        return "Dòng trạng thái";
      case "album":
        return "Album ảnh";
      default:
        return type;
    }
  };

  const filteredPosts = posts.filter((post) =>
    post.content.message?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const columns: ColumnsType<Post> = [
    {
      title: "Nội dung",
      key: "content",
      width: isMobile ? 250 : "40%",
      render: (_: any, record: Post) => (
        <Space align="start">
          {getPostIcon(record.content.type)}
          <div style={{ maxWidth: isMobile ? 200 : 400 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {getPostTypeText(record.content.type)}
              <Tag
                color={record.source === "synced" ? "blue" : "purple"}
                style={{ marginLeft: 8, fontSize: 10, lineHeight: "16px" }}
              >
                {record.source === "synced" ? "Facebook" : "App"}
              </Tag>
            </div>
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                fontSize: isMobile ? 12 : 13,
                color: "#555",
              }}
            >
              {record.content.message || (
                <Text type="secondary" italic>
                  Không có nội dung
                </Text>
              )}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Các trang",
      key: "relatedPages",
      width: 150,
      render: (_: any, record: Post) => {
        const relatedItems = record.relatedPosts || [];
        return (
          <Avatar.Group maxCount={isMobile ? 3 : 5} size="small">
            {relatedItems.map((p) => {
              const statusColor =
                p.status === "published"
                  ? "#52c41a"
                  : p.status === "failed"
                    ? "#ff4d4f"
                    : "#1890ff";
              return (
                <Tooltip
                  key={p.id}
                  title={
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.page.name}</div>
                      <div style={{ fontSize: 11 }}>
                        {p.status === "published"
                          ? "Đã đăng"
                          : p.status === "failed"
                            ? `Lỗi: ${p.errorMessage}`
                            : "Đang chờ"}
                      </div>
                    </div>
                  }
                >
                  <Avatar
                    src={p.page.pictureUrl}
                    style={{
                      border: `2px solid ${statusColor}`,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Avatar.Group>
        );
      },
    },
    {
      title: "Ngày đăng",
      dataIndex: "publishedAt",
      key: "publishedAt",
      width: 150,
      render: (date: string) => (
        <span style={{ fontSize: isMobile ? 12 : 14 }}>
          {new Date(date).toLocaleString("vi-VN")}
        </span>
      ),
    },
    {
      title: "Tương tác",
      key: "stats",
      width: 120,
      render: (_: any, record: Post) => (
        <Space size="middle" style={{ fontSize: isMobile ? 12 : 13 }}>
          <Tooltip title="Lượt thích">
            <span style={{ color: "#eb2f96" }}>
              <CheckCircleOutlined /> {record.stats?.likes || 0}
            </span>
          </Tooltip>
          <Tooltip title="Bình luận">
            <span style={{ color: "#1890ff" }}>
              <SyncOutlined /> {record.stats?.comments || 0}
            </span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Hành động",
      key: "action",
      fixed: (isMobile ? "right" : undefined) as any,
      width: 130,
      render: (_: any, record: Post) => (
        <Space>
          <Button
            type="primary"
            ghost
            icon={<EyeOutlined />}
            size={isMobile ? "small" : "middle"}
            onClick={() => {
              setSelectedPost(record);
              setDetailModalVisible(true);
            }}
          >
            {isMobile ? "" : "Chi tiết"}
          </Button>
          <Popconfirm
            title="Xóa bài viết"
            description="Bạn có chắc chắn muốn xóa bài viết này khỏi hệ thống?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              size={isMobile ? "small" : "middle"}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleModalClose = () => {
    setDetailModalVisible(false);
    setSelectedPost(null);
  };

  return (
    <Space
      direction="vertical"
      size={isMobile ? "middle" : "large"}
      style={{ width: "100%" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
        }}
      >
        <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>
          Danh sách bài viết
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchPosts}
          loading={loading}
          block={isMobile}
        >
          Làm mới
        </Button>
      </div>

      <Card bodyStyle={{ padding: isMobile ? "12px" : "24px" }}>
        <div
          style={{
            marginBottom: 24,
            display: "flex",
            gap: 16,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Input
            prefix={<SearchOutlined />}
            placeholder="Tìm kiếm nội dung bài viết..."
            style={{ width: isMobile ? "100%" : 300 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <PageSelect
            placeholder="Lọc theo trang"
            style={{ width: isMobile ? "100%" : 220 }}
            allowClear
            onChange={(value) => setFilterPageId(value)}
            suffixIcon={<FilterOutlined />}
            pages={pages}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredPosts}
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{
            size: isMobile ? "small" : undefined,
            pageSize: 10,
            showTotal: (total) =>
              isMobile ? `${total}` : `Tổng cộng ${total} bài viết`,
          }}
        />
      </Card>

      <Modal
        title="Chi tiết trạng thái đăng bài"
        open={detailModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose} block={isMobile}>
            Đóng
          </Button>,
        ]}
        width={isMobile ? "95%" : 700}
      >
        {selectedPost && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Card size="small" style={{ backgroundColor: "#fafafa" }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Nội dung Master
              </div>
              <div style={{ fontSize: 14 }}>{selectedPost.content.message}</div>
            </Card>

            <Table<RelatedPost>
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
              dataSource={selectedPost.relatedPosts}
              rowKey="id"
              columns={[
                {
                  title: "Trang",
                  key: "page",
                  width: 200,
                  render: (_, item: RelatedPost) => (
                    <Space>
                      <Avatar src={item.page.pictureUrl} size="small" />
                      <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
                        {item.page.name}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: "Trạng thái",
                  key: "status",
                  width: 150,
                  render: (_, item: RelatedPost) => {
                    let color: any = "blue";
                    let icon = <SyncOutlined spin />;
                    let label = "Đang chờ";

                    if (item.status === "published") {
                      color = "success";
                      icon = <CheckCircleOutlined />;
                      label = "Đã đăng";
                    } else if (item.status === "failed") {
                      color = "error";
                      icon = <CloseCircleOutlined />;
                      label = "Thất bại";
                    }

                    return (
                      <Tooltip title={item.errorMessage}>
                        <Tag
                          color={color}
                          icon={icon}
                          style={{ fontSize: isMobile ? 11 : 12 }}
                        >
                          {label}
                        </Tag>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: "Thời gian",
                  key: "time",
                  width: 150,
                  render: (_, item: RelatedPost) =>
                    item.status === "pending" ? (
                      <Text
                        type="secondary"
                        style={{ fontSize: isMobile ? 12 : 14 }}
                      >
                        <ClockCircleOutlined /> Đang chờ...
                      </Text>
                    ) : (
                      <Text style={{ fontSize: isMobile ? 12 : 14 }}>
                        {new Date(item.publishedAt).toLocaleString("vi-VN")}
                      </Text>
                    ),
                },
                {
                  title: "Link",
                  key: "link",
                  width: 100,
                  render: (_, item: RelatedPost) =>
                    item.status === "published" &&
                    item.postId !== "PENDING" && (
                      <Button
                        type="link"
                        size="small"
                        icon={<GlobalOutlined />}
                        href={`https://facebook.com/${item.postId}`}
                        target="_blank"
                      >
                        {isMobile ? "FB" : "Xem trên FB"}
                      </Button>
                    ),
                },
              ]}
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
};

export default PostList;
